import { createRouter, createWebHistory } from "vue-router";
import type { Router } from "vue-router";
import type { Store } from "vuex";

import api from "@/api";
import { useAccessStore } from "@/stores/access";
import { useCustomerStore } from "@/stores/customer";
import { useFeaturesStore } from "@/stores/features";
import { useNavigationPromptStore } from "@/stores/navigationPrompt";
import {
    makeBootRedirect,
    makeConfirmDiscard,
    makeEnsureCustomer,
    makeEnsureUser,
    makeRequireSection,
} from "./guards";
import type { BootResult } from "./guards";
import { routes } from "./routes";

export const createAppRouter = (
    store: Store<any>,
    booted: BootResult
): Router => {
    const router = createRouter({
        history: createWebHistory(),
        routes,
    });

    // Guards run in registration order, and this one is load-bearing:
    // confirmDiscard has to ask before ensureUser replaces the user it is
    // asking about, and requireSection and ensureCustomer both read what
    // ensureUser loaded.
    router.beforeEach(makeBootRedirect({ booted }));
    router.beforeEach(
        makeConfirmDiscard({ store, prompt: useNavigationPromptStore() })
    );
    router.beforeEach(makeEnsureUser({ store }));
    router.beforeEach(
        makeRequireSection({
            store,
            features: useFeaturesStore(),
            access: useAccessStore(),
        })
    );
    router.beforeEach(
        makeEnsureCustomer({ store, customerStore: useCustomerStore(), api })
    );

    // Without this a failed navigation is an unhandled rejection, which
    // index.html answers by hiding the whole app.
    router.onError((error) => {
        console.error("navigation failed", error);
    });

    return router;
};
